import { memo, useLayoutEffect, useMemo, useRef, useState } from "react"

import { get, isNil } from "lodash-es";
import type { ColDef, Column, ColumnMovedEvent, ColumnResizedEvent, FirstDataRenderedEvent, ProcessCellForExportParams, RowClassParams, SortChangedEvent } from "@ag-grid-community/core";
import { AgGridReact, CustomNoRowsOverlayProps } from "@ag-grid-community/react";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { RangeSelectionModule } from "@ag-grid-enterprise/range-selection";
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { Box, Button, SearchIcon, TextField, Tooltip } from "@parspec/pixel";

import type { BodItem, GroupedLotData, GroupedManufacturerData, TableItem } from "./types";
import type { TableCol } from "./derived-types";
import ViewBySelect, { VIEW_OPTIONS, ViewOptionValues } from "../shared/ViewBySelect";
import { getCellPercentFormatter, getRowId } from "../../shared/utils";
import { CustomTableHeader } from "src/features/shared/CustomTableHeader";
import { getCenteredCellStyle, commonCellStyle, defaultColDef, STYLE_CONTSANTS, AGG_FOOTER_ROW_ID, TABLE_COL_IDS } from "./constants";
import { applyInitialSortStateToTable, formatDollarAmount, getAggregatedRowData, getLotName, getOverlappingManufacturersWithLots } from "./utils";
import { stringComparator } from "src/features/Dashboard/shared/utils";
import { LotNameCellRenderer, ManufacturerCellRenderer } from "./components";
import { ManufacturerViewNoOverlay } from "./ManufacturerViewNoOverlay";
import { useGetUserProfileInfoQuery } from "src/features/Settings/MyProfile/queries";
import { EVENT_SERVICE_PAGENAME_VALUES } from "src/features/shared/utils/constants";
import { useGetSelectedColumnsAndPersistingFiltersQuery } from "src/features/shared/queries";
import { useSortChange } from "./hooks";
import { SortPayload } from "src/features/Dashboard/queries/apiTypes";
import { useUpdateColumnsOrderMutation, useUpdateColumnsWidthMutation } from "../queries";
import { LotsResponse } from "../../shared/types";
import { LoadingOverlayComponent } from "../../shared/commonTemplates";
import { SectionApiResponseType } from "../../queries/apiTypes";

interface ManufacturerViewProps {
    bods?: Array<BodItem>;
    lotData?: Array<LotsResponse>;
    isTableLoading: boolean;
    onChangeTableView: (value: ViewOptionValues) => void;
    sectionKey: 'section' | 'substitute_section';
    tableContainerDimensions: { width: number; height: number; } | null;
    sections?: Array<SectionApiResponseType>;
};

const MODULES = [ClientSideRowModelModule, RowGroupingModule, ClipboardModule, RangeSelectionModule];

const COLUMN_DEFS: Record<TableCol, ColDef<TableItem>> = {
    [TABLE_COL_IDS.MANUFACTURER]: {
        headerName: 'Manufacturer',
        colId: TABLE_COL_IDS.MANUFACTURER,
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableSorting: true,
        },
        minWidth: 120,
        valueGetter: (props) => props?.data?.manufacturers,
        getQuickFilterText: (params) => params.data.manufacturers.join(""),
        cellStyle: commonCellStyle,
        cellRenderer: ManufacturerCellRenderer,
        comparator(listA?: Array<string>, listB?: Array<string>) {
            // As there can be multiple manufacturers in each node, first sort them internally
            const sortedListA = [...(listA || [])].sort(stringComparator);
            const sortedListB = [...(listB || [])].sort(stringComparator);
            return stringComparator(sortedListA.join(""), sortedListB.join(""));
        },
    },
    [TABLE_COL_IDS.EXTENDED_COST]: {
        headerName: 'Ext. Cost',
        field: TABLE_COL_IDS.EXTENDED_COST,
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableSorting: true,
            justifyHeaderContent: "flex-end",
        },
        minWidth: 100,
        cellStyle: getCenteredCellStyle,
        valueFormatter: formatDollarAmount,
        getQuickFilterText: formatDollarAmount,
    },
    [TABLE_COL_IDS.EXTENDED_DISCOUNTED_COST]: {
        headerName: 'Ext. Discounted Cost',
        field: TABLE_COL_IDS.EXTENDED_DISCOUNTED_COST,
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableSorting: true,
            justifyHeaderContent: "flex-end",
        },
        minWidth: 120,
        cellStyle: getCenteredCellStyle,
        valueFormatter: formatDollarAmount,
        getQuickFilterText: formatDollarAmount,
    },
    [TABLE_COL_IDS.MARGIN]: {
        headerName: 'Margin (%)',
        field: TABLE_COL_IDS.MARGIN,
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableSorting: true,
            justifyHeaderContent: "flex-end",
        },
        minWidth: 100,
        cellStyle: getCenteredCellStyle,
        valueFormatter: getCellPercentFormatter,
        getQuickFilterText: getCellPercentFormatter,
    },
    [TABLE_COL_IDS.EXTENDED_REVENUE]: {
        headerName: 'Ext. Revenue',
        field: TABLE_COL_IDS.EXTENDED_REVENUE,
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableSorting: true,
            justifyHeaderContent: "flex-end",
        },
        minWidth: 130,
        cellStyle: getCenteredCellStyle,
        valueFormatter: formatDollarAmount,
        getQuickFilterText: formatDollarAmount,
    },
    [TABLE_COL_IDS.EXTENDED_PRICE]: {
        headerName: 'Ext. Sell Price',
        field: TABLE_COL_IDS.EXTENDED_PRICE,
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableSorting: true,
            justifyHeaderContent: "flex-end",
        },
        minWidth: 130,
        cellStyle: getCenteredCellStyle,
        valueFormatter: formatDollarAmount,
        getQuickFilterText: formatDollarAmount,
    },
    [TABLE_COL_IDS.LOT_ID]: {
        headerName: 'Lots',
        colId: TABLE_COL_IDS.LOT_ID,
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableSorting: true,
        },
        minWidth: 120,
        getQuickFilterText: (params) => params.data.lots.map(getLotName).join(""),
        cellStyle: commonCellStyle,
        // Note: Intentionally Omitting valueGetter to ensure re-render on node data change due to multiple data dependencies needed in cellRenderer.
        cellRenderer: LotNameCellRenderer,
        comparator(_a, _b, nodeA, nodeB) {
            // As there can be multiple lots in each node, first sort them internally
            const lotsA = nodeA.data?.lots.map(getLotName).sort(stringComparator);
            const lotsB = nodeB.data?.lots.map(getLotName).sort(stringComparator);
            return stringComparator((lotsA || []).join(""), (lotsB || []).join(""));
        },
    },
    [TABLE_COL_IDS.BLANK_COL]: {
        // This column is only added to prevent the blank space from being visible in the table,
        // when all other column widths sum up to less than the total available width.
        headerName: "",
        colId: TABLE_COL_IDS.BLANK_COL,
        flex: 1,
        resizable: false,
        suppressMovable: true,
        lockPosition: "right",
    },
};

const DEFAUlT_COL_ORDER = Object.keys(COLUMN_DEFS) as Array<TableCol>;

const AG_GRID_VIEWPORT_CLASS = ".ag-body-viewport";

const STATIC_HEIGHTS = {
    ACC_HEADER: 64, // Height of header of the current Accordion.
    MISC: 24, // 12px top spacing of Outer Box + 12px bttom padding of this Box
    TABLE_MISC: 45, // input height 37(36.7) + 8px gap
};

const extraElementHeights = Object.values(STATIC_HEIGHTS).reduce((acc, cur) => acc + cur, 0);

const getRowHeight = (multiplier: number) => STYLE_CONTSANTS.LINE_HEIGHT * multiplier + (STYLE_CONTSANTS.P_BLOCK * 2);

const getColsRequiredWidth = (columns?: Array<Column<TableItem>> | null) => {
    if (!columns) return 0;
    return columns.reduce((total, col) => total + (col.getActualWidth() || 0), 0);
}
// Prevents the combination of borders with the top border of the bottom pinned row
const getRowStyle = (params: RowClassParams<TableItem>) => params.node.lastChild
    ? { border: "none" } : undefined;

function ManufacturerViewTable(props: ManufacturerViewProps) {
    const { bods, isTableLoading, sectionKey, lotData, tableContainerDimensions, sections, onChangeTableView } = props;

    const [isGridReady, setIsGridReady] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [isSortingApplied, setIsSortingApplied] = useState(false);
    const [isHorizontalScrollVisible, setIsHorizontalScrollVisible] = useState(true);

    const tableRef = useRef<AgGridReact<TableItem> | null>(null);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);

    const { data: userData, isLoading: isUserDataLoading } = useGetUserProfileInfoQuery();

    const {
        data: selectedColumnsAndFiltersQueryData,
        isLoading: isUserTablePrefsLoading,
    } = useGetSelectedColumnsAndPersistingFiltersQuery({
        userId: userData?.data?.id || 0,
        pageName: EVENT_SERVICE_PAGENAME_VALUES.BOM_PRICING_MANUFACTUER_VIEW,
    },
        { enabled: !isUserDataLoading }
    );

    const { handleSortChanged: onSortChanged } = useSortChange(
        EVENT_SERVICE_PAGENAME_VALUES.BOM_PRICING_MANUFACTUER_VIEW, userData?.data.id ?? 0, selectedColumnsAndFiltersQueryData
    );
    const { mutate: updateColumnsOrder } = useUpdateColumnsOrderMutation();
    const { mutate: updateColumnsWidth } = useUpdateColumnsWidthMutation();

    const isLoading = isTableLoading || isUserDataLoading || isUserTablePrefsLoading;

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchText(e.target.value);
    };

    const lotsMap = useMemo(() => {
        if (!lotData) return undefined;

        return lotData.reduce((resultMap, lot) => {
            resultMap.set(lot.id, lot);
            return resultMap;
        }, new Map<number, LotsResponse>());
    }, [lotData]);

    const rowData = useMemo(() => {
        if (!bods || !lotsMap || !sections) return undefined;

        const { noKitBodsBySectionMap, kitMap } = bods.reduce<{
            kitMap: Map<number, BodItem>,
            noKitBodsBySectionMap: Map<number, Array<BodItem>>,
        }>(
            (result, item) => {
                const sectionId = item[sectionKey];
                if (isNil(sectionId)) return result;

                const bods = result.noKitBodsBySectionMap.get(sectionId);
                if (!bods) return result;

                if (item.is_kit) result.kitMap.set(item.id, item);
                else bods.push(item);
                return result;
            }, {
            kitMap: new Map(),
            noKitBodsBySectionMap: new Map(sections.map(section => [section.id, []])),
        }
        );

        const noKitBods: Array<BodItem> = [...noKitBodsBySectionMap.values()].flatMap(bods => bods);
        const groupedData = noKitBods.reduce((result, bod) => {
            const { groupedManufacturers, groupedLotIds } = result;
            const { manufacturer, kit } = bod;

            const isHidden = !isNil(kit)
                // In case of component, check both if parent kit or the component itself is hidden.
                ? (kitMap.get(kit) ?? { is_hidden: true }).is_hidden || bod.is_hidden
                : bod.is_hidden;

            if (isHidden) return result;

            // If this is component, get lotId from parent Kit.
            const lotId = !isNil(kit) ? kitMap.get(kit)?.lotId : bod.lotId;

            const manufacturerKey = manufacturer.trim() || "-";
            const manufacturerGroup = groupedManufacturers.get(manufacturerKey)
                ?? { bods: [], lotIds: new Set() };

            // Add bod to this Manufacturer group
            manufacturerGroup.bods.push(bod);

            if (!isNil(lotId)) {
                // Add this lot this Manufacturer group
                manufacturerGroup.lotIds.add(lotId);

                // Get and add the manufacturer to grouped lots map
                const lotGroup = groupedLotIds.get(lotId) ?? new Set<string>();
                lotGroup.add(manufacturerKey);
                groupedLotIds.set(lotId, lotGroup);
            }

            groupedManufacturers.set(manufacturerKey, manufacturerGroup);
            return result;
        }, {
            // to keep track of set of bods and lotIds per single manufacturer
            groupedManufacturers: new Map<string, GroupedManufacturerData>(),
            // to keep track of set of manufacturers per single group Id
            groupedLotIds: new Map<number, GroupedLotData>(),
        });

        const combinedResult = getOverlappingManufacturersWithLots(
            groupedData.groupedManufacturers,
            groupedData.groupedLotIds,
        );

        const rows = combinedResult.map<TableItem>(
            combinedItem => getAggregatedRowData(combinedItem, lotsMap, kitMap)
        );

        return rows;
    }, [bods, lotsMap, sections, sectionKey]);

    const totalFooterRow = useMemo(() => {
        if (!rowData) return undefined;

        // NOTE: Can't use Aggrid agg footer row, as it is not pinned in the version we using.
        const footerRowData = rowData.reduce<TableItem>((result, row) => {
            const { extendedCost, extendedDiscountCost, extendedRevenue, extendedPrice } = row;
            result.extendedCost += extendedCost;
            result.extendedDiscountCost += extendedDiscountCost;
            result.extendedRevenue += extendedRevenue;
            result.extendedPrice += extendedPrice;

            return result;
        }, {
            id: AGG_FOOTER_ROW_ID,
            extendedCost: 0,
            extendedDiscountCost: 0,
            margin: 0,
            extendedRevenue: 0,
            extendedPrice: 0,
            manufacturers: ["Subtotal"],
            lots: [],
        });

        const { extendedDiscountCost, extendedPrice } = footerRowData;
        const margin = Number(((1 - extendedDiscountCost / extendedPrice) * 100).toFixed(2));

        footerRowData.margin = Number.isFinite(margin) ? margin : 0;
        return [footerRowData];
    }, [rowData]);

    const isEmptyRows = !rowData?.length;
    useLayoutEffect(() => {
        if (!isGridReady) return;

        let timeout: NodeJS.Timeout;
        if (isLoading) {
            tableRef.current?.api?.showLoadingOverlay();
        } else if (isEmptyRows) {
            timeout = setTimeout(() => {
                tableRef.current?.api?.showNoRowsOverlay();
            });
        } else {
            tableRef.current?.api?.hideOverlay();
            // Ask the table to recalculate heights on change of rows
            // as the cells have the dynamic height and could have changed.
            tableRef.current?.api.resetRowHeights();
        }

        return () => clearTimeout(timeout);
    }, [isGridReady, isLoading, isEmptyRows]);

    const onFirstDataRendered = (params: FirstDataRenderedEvent<TableItem>) => {
        const sortField = get(
            selectedColumnsAndFiltersQueryData,
            "table_settings[0].sort_field",
            null
        ) as (SortPayload | null);

        const gridWidth = tableContainerRef.current?.querySelector(AG_GRID_VIEWPORT_CLASS)?.clientWidth;
        if (!isNil(gridWidth)) {
            const columnsRequiredWidth = getColsRequiredWidth(tableRef.current?.api.getColumns());
            setIsHorizontalScrollVisible(columnsRequiredWidth > gridWidth);
        }
        applyInitialSortStateToTable(params, sortField);
    };

    const handleSortChanged = (params: SortChangedEvent) => {
        setIsSortingApplied(tableRef.current?.api.getColumnState().some((columnState) => columnState.sort) || false);
        onSortChanged(params);
    }

    const handleColumnMoved = async (props: ColumnMovedEvent<TableItem>) => {
        if (props.finished && props.source === 'uiColumnMoved') {
            const columnState = props.api.getColumnState();
            const newColumnOrder = columnState.map((column) => column.colId) as Array<TableCol>;
            updateColumnsOrder({
                pageName: EVENT_SERVICE_PAGENAME_VALUES.BOM_PRICING_MANUFACTUER_VIEW,
                order: newColumnOrder.filter(colId => colId !== TABLE_COL_IDS.BLANK_COL),
            });
        }
    };

    const handleColumnResized = (e: ColumnResizedEvent<TableItem>) => {
        if (e.finished) {
            if (e.source === 'uiColumnResized' || e.source === 'flex') {
                const gridWidth = tableContainerRef.current?.querySelector(AG_GRID_VIEWPORT_CLASS)?.clientWidth;
                if (!isNil(gridWidth)) {
                    const columnsRequiredWidth = getColsRequiredWidth(tableRef.current?.api.getColumns());
                    setIsHorizontalScrollVisible(columnsRequiredWidth > gridWidth);
                }
            }

            if (e.source === 'uiColumnResized') {
                const columnWidths = e.api.getColumnState().reduce((acc, col) => {
                    // In case user hasn't set this columns width manually.
                    // Let it remain flex and don't pass to server to save.
                    if (!col.flex && col.width) {
                        acc[col.colId as TableCol] = col.width;
                    }
                    return acc;
                }, {} as Record<TableCol, number>);
                updateColumnsWidth({
                    pageName: EVENT_SERVICE_PAGENAME_VALUES.BOM_PRICING_MANUFACTUER_VIEW,
                    widthPayload: columnWidths,
                });
            }
        };
    };

    const processCellForClipboard = (e: ProcessCellForExportParams<TableItem>) => {
        const columnId = e.column.getColId() as TableCol;
        if (columnId === TABLE_COL_IDS.LOT_ID) {
            return e.node?.data?.lots.map(lot => lot.name).join(", ") || "-";
        }

        return e.value;
    };

    const handleOnClearClick = () => {
        setSearchText("");
        tableRef.current?.api.applyColumnState({
            defaultState: { sort: null }
        });
    };

    const columnDefs = useMemo(() => {
        if (isUserTablePrefsLoading) return null;

        const serverColOrder: null | undefined | Array<TableCol> = get(
            selectedColumnsAndFiltersQueryData,
            'table_settings[0].order'
        );

        const widthsObj: null | undefined | Record<TableCol, number> = get(
            selectedColumnsAndFiltersQueryData,
            "table_settings[0].width",
        );

        const order = Array.isArray(serverColOrder) && serverColOrder.length
            ? serverColOrder : DEFAUlT_COL_ORDER;
        const filteredOrder = order.filter(item => item !== TABLE_COL_IDS.BLANK_COL);

        const colDefs = [
            ...filteredOrder.map(colKey => {
                const column = COLUMN_DEFS[colKey];
                if (!column) return column;

                const width = widthsObj?.[colKey];
                return width
                    ? { ...column, flex: 0, width } as ColDef<TableItem>
                    : column;
            }).filter(col => !isNil(col)),
            COLUMN_DEFS[TABLE_COL_IDS.BLANK_COL], // always put blank column at the end
        ];
        return colDefs;
    }, [isUserTablePrefsLoading, selectedColumnsAndFiltersQueryData]);

    const tableHeight = useMemo(() => {
        const rowsLength = rowData?.length;

        if (!rowsLength || !tableContainerDimensions?.height) return 317;

        const horizontalScrollHeight = 12;

        // With the use of pinned cols, we cannot leave the empty space for horizontal scroll when it's not visible
        // because that empty space renders b/w all normal rows and pinned row, which looks odd. So that's by calculating
        // height based on if horizontal scroll visible or not.
        const headerHeight = isHorizontalScrollVisible ? 35 + horizontalScrollHeight : 35;
        const aggRowHeight = 36;

        const rowsHeight = rowData.reduce((acc, row) => {
            return acc + getRowHeight(row.manufacturers.length);
        }, 0);

        const totalHeight = rowsHeight + headerHeight + aggRowHeight;
        const maxTableHeight = tableContainerDimensions.height - extraElementHeights;

        return Math.min(totalHeight, maxTableHeight);
    }, [rowData, isHorizontalScrollVisible, tableContainerDimensions?.height]);

    return (
        <Box display="flex" flexDirection="column" gap={2} paddingBottom={3}>
            <Box display="flex" gap={4} justifyContent="space-between">
                <Box display="flex" alignItems="center">
                    <Box width={300}>
                        <TextField
                            label=""
                            placeholder="Search..."
                            startIcon={<SearchIcon fontSize="small" />}
                            size="small"
                            value={searchText}
                            onChange={handleSearchChange}
                        />
                    </Box>
                    {isSortingApplied || searchText.trim() ?
                        (
                            <Tooltip title="Clear Search and Sorting">
                                <Button onClick={handleOnClearClick} variant="text" color="primary">
                                    Clear
                                </Button>
                            </Tooltip>
                        ) : null}
                </Box>

                <Box width={140}>
                    <ViewBySelect
                        // Passing 'Manufacturer' as it's the only value this component will have.
                        // onChange will anyways unmount this whole component.
                        value="Manufacturer"
                        onChange={e => onChangeTableView(e.target.value as ViewOptionValues)}
                    />
                </Box>
            </Box>
            <Box
                ref={tableContainerRef}
                height={tableHeight}
                minHeight={100}
                width="100%"
                className="ag-theme-quartz"
                sx={{
                    // update the bg color of nested table(just for this screen) as per design
                    '--ag-background-color': '#f1f5f9',
                }}
            >
                <AgGridReact<TableItem>
                    ref={tableRef}
                    // Explicitly passing undefined to wait for all the things to fetch before showing data.
                    rowData={isLoading ? undefined : rowData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    onGridReady={() => setIsGridReady(true)}
                    onFirstDataRendered={onFirstDataRendered}
                    modules={MODULES}
                    loadingOverlayComponent={LoadingOverlayComponent}
                    noRowsOverlayComponent={(props: CustomNoRowsOverlayProps) => (
                        <ManufacturerViewNoOverlay
                            {...props}
                            goToSectionView={() => onChangeTableView(VIEW_OPTIONS.SECTION)}
                        />
                    )}
                    suppressContextMenu
                    enableRangeSelection
                    suppressDragLeaveHidesColumns
                    pinnedBottomRowData={totalFooterRow}
                    defaultColDef={defaultColDef}
                    quickFilterText={searchText}
                    getRowStyle={getRowStyle}
                    processCellForClipboard={processCellForClipboard}
                    getRowHeight={(params) => getRowHeight(params.data?.manufacturers.length || 1)}
                    suppressMenuHide
                    onSortChanged={handleSortChanged}
                    onColumnResized={handleColumnResized}
                    onColumnMoved={handleColumnMoved}
                />
            </Box>
        </Box>
    );
};

export default memo(ManufacturerViewTable);